// WalkRequest.js
import React, { Fragment, useEffect, useReducer, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPhoneAlt,
  faClock,
  faBath,
  faToolbox,
  faMapMarkerAlt,
  faPaw,
  faPen,
  faCalendarAlt,
  faShieldDog,
  faInfoCircle,
  faChevronDown,
  faChevronUp,
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Dialog, Transition } from "@headlessui/react";
import {
  Autocomplete,
  GoogleMap,
  Marker,
  useLoadScript,
} from "@react-google-maps/api";
import { CURRENCY_CODE, GoogleMapStyle } from "../../../config/constants";
import { motion } from "framer-motion";
import ReactPhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { PetSelection } from "../../../components/walk/PetSelection";
import { DurationSelector } from "../../../components/walk/DurationSelector";
import { ToolSelectionWithTotal } from "../../../components/walk/ToolSelectionWithTotal";
import { PromoCodeInput } from "../../../components/PromoCodeInput";
import { StepWizard } from "../../../components/StepWizard";
import { usePromoCode } from "../../../hooks/usePromoCode";
import api from "../../../api/api";
import { PickupLocationIcon } from "../../../config/googlemapstyle";
import Schedule from "./Schedule";
// Import the reducer and its initial state (adjust the path as needed)
import { walkReducer, initialState } from "../../../reducers/walkReducer";
import { convertToAmPm, routes } from "../../../config/utility";
import WalkPaymentMethodSelection from "./WalkPaymentMethod";
import { format } from 'date-fns';


// const steps = [
//   { number: 1, label: "Pickup Location" },
//   { number: 2, label: "Pets Selection" },
//   { number: 3, label: "Duration Selection" },
//   { number: 4, label: "Tools & Extras" },
//   { number: 5, label: "Details & Coupons" },
//   { number: 6, label: "Schedule Walk" },
//   { number: 7, label: "Summary & Submit" },
//   { number: 8, label: "Payment Method" },
// ];

const steps = [
  { number: 1, label: "Location" },
  { number: 2, label: "Pets" },
  { number: 3, label: "Duration" },
  { number: 4, label: "Tools" },
  { number: 5, label: "Details" },
  { number: 6, label: "Schedule" },
  { number: 7, label: "Summary" },
  { number: 8, label: "Payment" },
];

const libraries = ["places"];

const WalkRequest = ({ item, onSave, onCancel, pets, tools, permission, userUpdateContext }) => {
  const applyFreeWalk = permission.FREE_PAW_WALK > 0;
  const baseRate = parseFloat(permission.PAW_WALK_BASE_RATE);
  const cleanPetCharge = parseFloat(permission.PAW_WALK_CLEAN_PET);
  // const freeChargeKeys = ["duration", "pet", "tool", "clean_pet"];
  const freeChargeKeys = [];




  // Use useReducer to manage all form fields.
  const [state, dispatch] = useReducer(walkReducer, initialState);

  // Other states you still need
  const [walkData, setWalkData] = useState({});
  // Replace the simple useState with a custom state handler
  const [step, setStepState] = useState(1);
  // Create a custom step setter function
  const setStep = (newStep) => {
    if (step === 8) {
      return;
    }
    setStepState(newStep);
  };

  const [total, setTotal] = useState(0);
  const [currentPosition, setCurrentPosition] = useState({ lat: null, lng: null });
  const [autocompletePickup, setAutocompletePickup] = useState(null);
  const [loading, setLoading] = useState(false);
  const [locating, setLocating] = useState(false);
  const [showMainReorderInfo, setShowMainReorderInfo] = useState(true);



  // Local state for charges (unchanged)
  const [charges, setCharges] = useState({
    base_rate: permission.PAW_WALK_BASE_RATE,
    location: 0,
    duration: 0,
    pet: 0,
    clean_pet: 0,
    tool: 0,
  });

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries,
  });

  const {
    discount,
    discountAmount,
    promoError,
    promoSuccess,
    applyPromoCode,
    calculateDiscount,
    calculateSpecicalDiscount,
  } = usePromoCode(charges, CURRENCY_CODE);

  const vatRate = 0.05; // 5% VAT
  const subTotal = applyFreeWalk ? total - discountAmount : total + baseRate - discountAmount;
  const vatAmount = subTotal * vatRate;
  const finalTotal = subTotal + vatAmount;


  // Get current location and update the pickup fields via dispatch.
  // useEffect(() => {
  //   const timeoutId = setTimeout(() => {
  //     navigator.geolocation.getCurrentPosition(
  //       (position) => {
  //         const { latitude, longitude } = position.coords;
  //         setCurrentPosition({ lat: latitude, lng: longitude });
  //         const geocoder = new window.google.maps.Geocoder();
  //         geocoder.geocode(
  //           { location: { lat: latitude, lng: longitude } },
  //           (results, status) => {
  //             if (status === "OK" && results[1]) {
  //               dispatch({
  //                 type: "UPDATE_FIELD",
  //                 field: "pickup_address",
  //                 payload: results[1].formatted_address,
  //               });
  //               dispatch({
  //                 type: "UPDATE_FIELD",
  //                 field: "pickup_latitude",
  //                 payload: latitude,
  //               });
  //               dispatch({
  //                 type: "UPDATE_FIELD",
  //                 field: "pickup_longitude",
  //                 payload: longitude,
  //               });
  //             }
  //           }
  //         );
  //       },
  //       (error) => console.error("Error getting current location", error)
  //     );
  //   }, 100);
  //   return () => clearTimeout(timeoutId);
  // }, []);

  // If an item is being edited or reordered, load its data into the reducer state.
  useEffect(() => {
    if (item) {
      const pickup_latitude = item.pickup_latitude || currentPosition.lat || 24.8607;
      const pickup_longitude = item.pickup_longitude || currentPosition.lng || 67.0011;

      // Check if this is a reorder (item already has the correct structure)
      if (item.isReorder) {
        dispatch({
          type: "LOAD_ITEM",
          payload: {
            ...item,
            pickup_latitude,
            pickup_longitude,
            // pet_ids and tool_ids are already in the correct format for reorder
          },
        });
      } else {
        // Original edit logic for existing walks
        dispatch({
          type: "LOAD_ITEM",
          payload: {
            ...item,
            pickup_latitude,
            pickup_longitude,
            pet_ids: item.pets ? item.pets.map((pet) => pet.id) : [],
            tool_ids: item.tools ? item.tools.map((tool) => tool.id) : [],
            scheduled_time: item.scheduled_at ? new Date(item.scheduled_at) : null,
          },
        });
      }
    }
  }, [item]);

  useEffect(() => {
    const calcTotal = applyFreeWalk
      ? Object.entries(charges)
        .filter(([key, chargeValue]) => chargeValue > 0 && key !== "base_rate" && !freeChargeKeys.includes(key))
        .reduce((total, entry) => total + Number(entry[1]), 0)
      : Object.entries(charges)
        .filter(([key]) => key !== "base_rate")
        .reduce((acc, [, value]) => acc + parseFloat(value), 0);
    setTotal(calcTotal);
    if (discount) {
      applyFreeWalk
        ? calculateDiscount(discount) // in future isko optimize kerna hai cause yahan calculateSpecicalDiscount tha jiski need khatan hou gaye
        : calculateDiscount(discount);
    }
  }, [charges, discount, applyFreeWalk, calculateDiscount, calculateSpecicalDiscount]);

  // When a place is selected, update the corresponding fields.
  const handlePlaceSelect = (place, type) => {
    if (!place.geometry) return;
    const lat = place.geometry.location.lat();
    const lng = place.geometry.location.lng();
    const address = place.formatted_address;
    if (type === "pickup") {
      dispatch({ type: "UPDATE_FIELD", field: "pickup_latitude", payload: lat });
      dispatch({ type: "UPDATE_FIELD", field: "pickup_longitude", payload: lng });
      dispatch({ type: "UPDATE_FIELD", field: "pickup_address", payload: address });
    } else if (type === "dropoff") {
      dispatch({ type: "UPDATE_FIELD", field: "dropoff_latitude", payload: lat });
      dispatch({ type: "UPDATE_FIELD", field: "dropoff_longitude", payload: lng });
      dispatch({ type: "UPDATE_FIELD", field: "dropoff_address", payload: address });
    }
  };

  // Helper function to get service labels.
  const getServiceLabel = (key) => {
    switch (key) {
      case "duration":
        return "Pet Walk Duration";
      case "pet":
        return "Additional Pet Fee";
      case "tool":
        return "Pet Walker’s Gear & Supplies";
      case "clean_pet":
        return "Charges For Cleaning Your Pet";
      default:
        return key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, " ");
    }
  };

  // When the pickup marker is dragged, update the location.
  const handlePickupDragEnd = (event) => {
    const lat = event.latLng.lat();
    const lng = event.latLng.lng();
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === "OK" && results[1]) {
        dispatch({
          type: "UPDATE_FIELD",
          field: "pickup_address",
          payload: results[1].formatted_address,
        });
      }
    });
    dispatch({ type: "UPDATE_FIELD", field: "pickup_latitude", payload: lat });
    dispatch({ type: "UPDATE_FIELD", field: "pickup_longitude", payload: lng });
  };

  // Validation logic per step (using state from the reducer)
  const validateStep = () => {
    switch (step) {
      case 1:
        if (!state.pickup_address || !state.pickup_latitude || !state.pickup_longitude) {
          toast.error("Pickup location is required");
          return false;
        }
        break;
      case 2:
        if (state.pet_ids.length === 0) {
          toast.error("Please select at least one pet");
          return false;
        }
        break;
      case 3:
        if (!state.duration_walk) {
          toast.error("Please select a duration for the walk");
          return false;
        }
        break;
      case 4:
        return true; // (Tools step validation can be added here if needed)
      case 5:
        if (!state.emergency_no) {
          toast.error("Please provide an emergency contact");
          return false;
        }
        break;
      case 6:
        if (state.scheduleType == 'slot') {
          if (!state.day) {
            toast.error("Please select a day");
            return false;
          }
          if (!state.walkSlot) {
            toast.error("Please select a slot");
            return false;
          }
        }

        if (state.scheduleType == 'datetime') {
          if (!state.scheduled_at) {
            toast.error("Please select a date time.");
            return false;
          }
        }
        return true;
      case 7:

        if (!validateAllFields()) {
          toast.error("Please fill out all required fields before submission");
          return false;
        }

        break;
      default:
        return true;
    }
    return true;
  };

  const validateAllFields = () => {
    return (
      state.pickup_address &&
      state.pickup_latitude &&
      state.pickup_longitude &&
      state.clean_pet &&
      state.scheduled_at &&
      state.emergency_no &&
      state.pet_ids.length > 0 &&
      state.duration_walk
    );
  };

  const handleNextStep = () => {
    if (validateStep()) {
      if (step < steps.length) {
        setStep(step + 1);
      } else {
        toast.success("Form submitted successfully!");
      }
    }
  };

  const handlePrevStep = () => setStep(step - 1);

  // Generic handler for input changes.
  const handleChange = (e) => {
    const { name, value } = e.target;
    dispatch({ type: "UPDATE_FIELD", field: name, payload: value });
  };

  // On form submission, send the state as the request data.
  const handleSubmit = async (e) => {
    e.preventDefault();
    const requestData = {
      ...state,
      waiting_time: state.waiting_time || "",
    };
    try {
      setLoading(true)
      // if (item) {
      //   await api.put(`/walks/${item.id}`, requestData);
      //   toast.success("Walk updated successfully");
      // } else {
      const response = await api.post("/walks", requestData);
      setWalkData(response.data)
      await userUpdateContext();
      if (response.data.charges?.total <= 3) {
        onSave();
      } else {
        setStep(8)
      }
      setLoading(false)
      toast.success("New walk request submitted successfully! A walker will be assigned once you have selected a payment method.");
      // }
      // onSave();
    } catch (error) {
      toast.error("Error saving walk. Please check the form.");
    } finally {
      setLoading(false)
    }
  };

  if (loadError) {
    return <div>Error loading Google Maps</div>;
  }
  if (!isLoaded) {
    return <div>Loading Maps...</div>;
  }

  return (
    <Transition appear show as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onCancel}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-30 backdrop-blur-sm" />
        </Transition.Child>
        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-7xl transform overflow-hidden rounded-2xl bg-purple-50 text-left align-middle shadow-md transition-all">
                {/* Reorder Indicator */}
                {item?.isReorder && (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mx-6 mt-4 shadow-sm">
                    <div
                      className="flex items-center justify-between cursor-pointer"
                      onClick={() => setShowMainReorderInfo(!showMainReorderInfo)}
                    >
                      <div className="flex items-center space-x-2">
                        <FontAwesomeIcon icon={faInfoCircle} className="text-purple-600" />
                        <p className="text-purple-800 font-semibold">🔄 Reordering Previous Walk</p>
                      </div>
                      <FontAwesomeIcon
                        icon={showMainReorderInfo ? faChevronUp : faChevronDown}
                        className="text-purple-600"
                      />
                    </div>

                    {showMainReorderInfo && (
                      <div className="mt-4 pt-4 border-t border-purple-200 space-y-3">
                        <div className="bg-white rounded-lg p-3 border border-purple-100">
                          <h4 className="text-purple-800 font-semibold mb-2 flex items-center text-sm">
                            ✅ Pre-filled Information:
                          </h4>
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-1 text-xs text-purple-700">
                            <div>• Pickup Location</div>
                            <div>• Selected Pets</div>
                            <div>• Walk Duration</div>
                            <div>• Tools & Extras</div>
                            <div>• Emergency Contact</div>
                            <div>• Pet Cleaning</div>
                          </div>
                        </div>

                        <div className="bg-purple-100 rounded-lg p-3 border border-purple-300">
                          <h4 className="text-purple-800 font-semibold mb-1 text-sm">
                            ⏰ Schedule Selection Required
                          </h4>
                          <p className="text-purple-700 text-xs">
                            Choose <strong>Instant Walk</strong> or select a <strong>new date & time</strong>.
                            Previous scheduling cannot be reused.
                          </p>
                        </div>

                        <div className="text-purple-600 text-xs">
                          💡 <strong>Tip:</strong> You can modify any information before submitting.
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Step Wizard */}
                <div className="flex items-center justify-between  sm:mb-0">
                  <StepWizard step={step} setStep={setStep} steps={steps} formData={state} />
                </div>
                <div className="mx-6">
                  <form onSubmit={handleSubmit}>
                    <div className="relative">
                      {step === 1 && (
                        <div className="sm:absolute sm:-top-16 sm:left-0 sm:right-0 z-10 sm:flex sm:justify-center sm:px-4 ">
                          <div className="w-full sm:w-2/3 lg:w-1/2 bg-bodyMain p-3 sm:p-4 rounded-lg shadow-lg backdrop-blur-md">
                            {/* Header Section */}
                            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-4 mb-3 sm:mb-2">
                              <label className="text-base sm:text-lg font-medium text-gray-900">
                                Pickup Location
                              </label>
                              <button
                                onClick={handleNextStep}
                                type="button"
                                className="w-full sm:w-auto bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 sm:p-3 rounded-lg transition-all duration-150 text-sm sm:text-base"
                              >
                                Next
                              </button>
                            </div>

                            {/* Autocomplete Input */}
                            <Autocomplete
                              onLoad={(ref) => setAutocompletePickup(ref)}
                              onPlaceChanged={() => {
                                const place = autocompletePickup?.getPlace();
                                handlePlaceSelect(place, "pickup");
                              }}
                            >
                              <input
                                type="text"
                                placeholder="Search for pickup location"
                                value={state.pickup_address}
                                onChange={(e) =>
                                  dispatch({
                                    type: "UPDATE_FIELD",
                                    field: "pickup_address",
                                    payload: e.target.value,
                                  })
                                }
                                className="block w-full p-2 sm:p-3 text-sm sm:text-base border border-gray-300 rounded-lg shadow-sm 
                              focus:ring-purple-600 focus:border-purple-600 transition-all duration-150
                              placeholder:text-sm sm:placeholder:text-base"
                              />
                            </Autocomplete>
                          </div>
                        </div>
                      )}
                      {step === 7 && (
                        <h2 className="text-2xl font-bold text-gray-800 my-6 text-center">
                          Review & Confirm Your Walk 📝
                        </h2>
                      )}
                      <div
                        className={`mt-4 sm:mt-10 bg-white shadow-lg rounded-lg ${step === 1 || step === 7 ? "block" : "hidden"
                          }`}
                      >
                        {/* Map Controls Overlay */}
                        {
                          step == 1 ? (
                            <div className="absolute bottom-4  sm:top-4 right-4 z-10 flex flex-col gap-2">
                              <div className="relative group flex flex-col items-center">
                                <button
                                  type="button"
                                  disabled={locating}
                                  onClick={() => {
                                    setLocating(true);
                                    navigator.geolocation.getCurrentPosition(
                                      (position) => {
                                        const { latitude, longitude } = position.coords;
                                        setCurrentPosition({ lat: latitude, lng: longitude });
                                        const geocoder = new window.google.maps.Geocoder();
                                        geocoder.geocode(
                                          { location: { lat: latitude, lng: longitude } },
                                          (results, status) => {
                                            setLocating(false);
                                            if (status === "OK" && results[1]) {
                                              dispatch({
                                                type: "UPDATE_FIELD",
                                                field: "pickup_address",
                                                payload: results[1].formatted_address,
                                              });
                                              dispatch({
                                                type: "UPDATE_FIELD",
                                                field: "pickup_latitude",
                                                payload: latitude,
                                              });
                                              dispatch({
                                                type: "UPDATE_FIELD",
                                                field: "pickup_longitude",
                                                payload: longitude,
                                              });
                                            }
                                          }
                                        );
                                      },
                                      (error) => {
                                        setLocating(false);
                                      }
                                    );
                                  }}
                                  className={`relative p-2 bg-white rounded-full shadow-lg transition-all duration-300 text-purple-600 focus:outline-none
      ${locating ? "opacity-60 cursor-not-allowed" : "hover:shadow-xl hover:bg-purple-50"}`}
                                  title="Use current location"
                                >
                                  {/* Animated pulse */}
                                  <span className="absolute -inset-1 rounded-full bg-purple-200 opacity-60 animate-ping z-0"></span>
                                  {locating ? (
                                    <svg className="animate-spin h-6 w-6 relative z-10 text-purple-400" fill="none" viewBox="0 0 24 24">
                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0116 0H4z"></path>
                                    </svg>
                                  ) : (
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 relative z-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                    </svg>
                                  )}
                                </button>
                                {/* Tooltip above button */}
                                <div className="absolute -top-24 left-1/2 -translate-x-1/2 flex flex-col items-center z-20 pointer-events-none">
                                  <span className="bg-gray-900 text-white text-xs rounded-md px-3 py-1 shadow-lg opacity-50 group-hover:opacity-100 transition-opacity duration-200 font-medium">
                                    Click to use your current location
                                  </span>
                                  {/* Arrow */}
                                  <span className="w-3 h-3 bg-gray-900 rotate-45 mt-[-6px] shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200"></span>
                                </div>
                              </div>
                            </div>
                          ) : null
                        }


                        <GoogleMap
                          center={
                            step === 1 && state.pickup_latitude
                              ? { lat: state.pickup_latitude, lng: state.pickup_longitude }
                              : { lat: state.pickup_latitude, lng: state.pickup_longitude }
                          }
                          zoom={step === 1 ? 14 : 13}
                          mapContainerStyle={{
                            width: "100%",
                            height: "500px",
                            borderRadius: "12px",
                            boxShadow: "0 4px 10px rgba(0, 0, 0, 0.1)",
                          }}
                          options={{ styles: GoogleMapStyle, disableDefaultUI: true, zoomControl: true }}
                          onLoad={(map) => {
                            if (step === 7 && state.pickup_latitude) {
                              const bounds = new window.google.maps.LatLngBounds();
                              bounds.extend({ lat: state.pickup_latitude, lng: state.pickup_longitude });
                              map.fitBounds(bounds);
                            }
                          }}
                        >
                          {state.pickup_latitude && (
                            <Marker
                              position={{ lat: state.pickup_latitude, lng: state.pickup_longitude }}
                              draggable={true}
                              onDragEnd={handlePickupDragEnd}
                              icon={{
                                url: PickupLocationIcon,
                                scaledSize: new window.google.maps.Size(56, 76),
                              }}
                            />
                          )}
                        </GoogleMap>
                      </div>
                    </div>
                    {step >= 2 && (
                      <div className="mb-4 p-3">



                        {step === 2 && (
                          <motion.div initial={{ opacity: 0, x: -50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5 }}>
                            <PetSelection
                              permission={permission}
                              state={state}
                              pets={pets}
                              dispatch={dispatch}
                              setCharges={setCharges}
                            />
                          </motion.div>
                        )}



                        {step === 3 && (
                          <motion.div initial={{ opacity: 0, x: -50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5 }}>
                            <DurationSelector
                              permission={permission}
                              state={state}
                              dispatch={dispatch}
                              setCharges={setCharges}
                            />
                          </motion.div>
                        )}



                        {step === 4 && (
                          <motion.div initial={{ opacity: 0, x: -50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5 }}>
                            <ToolSelectionWithTotal state={state} tools={tools} dispatch={dispatch} setCharges={setCharges} />
                          </motion.div>
                        )}



                        {step === 5 && (
                          <motion.div
                            initial={{ opacity: 0, x: -50 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.5 }}
                            className="px-1 sm:px-6 py-4"
                          >
                            {/* Clean Pet Section */}
                            <div className="mb-2 sm:mb-3">
                              <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 p-4  rounded-lg ">
                                <h2 className="text-base sm:text-lg font-semibold text-gray-700">
                                  Clean Up Your Pet? 🛁
                                </h2>
                                <div className="flex items-center">
                                  <input
                                    type="checkbox"
                                    name="clean_pet"
                                    checked={state.clean_pet}
                                    onChange={(e) => {
                                      const isChecked = e.target.checked;
                                      dispatch({ type: "UPDATE_FIELD", field: "clean_pet", payload: isChecked });
                                      setCharges((prev) => ({ ...prev, clean_pet: isChecked ? cleanPetCharge : 0 }));
                                    }}
                                    className="h-5 w-5 border-gray-300 rounded focus:ring-purple-500"
                                  />
                                  <div className="ml-3 flex flex-col">
                                    <span className="text-sm sm:text-base text-gray-700 font-medium flex items-center">
                                      Yes, I want my pet cleaned with wipes after the walk.
                                    </span>
                                    <span className="text-xs text-gray-500 mt-1">
                                      <FontAwesomeIcon icon={faInfoCircle} className="mr-1 text-purple-400" />
                                      This is an additional service and will add <span className="font-semibold text-purple-700">{cleanPetCharge.toFixed(2)} {CURRENCY_CODE}</span> to your total.
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Emergency Contact Section */}
                            <div className="mb-6 sm:mb-8">
                              <div className=" p-4 rounded-lg ">
                                <h2 className="text-base sm:text-lg font-semibold text-gray-700 mb-4">
                                  Emergency Contact{" "}
                                  <span role="img" aria-label="emergency">
                                    🚨
                                  </span>
                                </h2>
                                <div className="relative">
                                  <ReactPhoneInput
                                    country={"ae"}
                                    value={state.emergency_no}
                                    onChange={(value) =>
                                      dispatch({ type: "UPDATE_FIELD", field: "emergency_no", payload: value })
                                    }
                                    inputStyle={{
                                      width: "100%",
                                      padding: "12px 12px 12px 50px",
                                      border: "1px solid #d1d5db",
                                      borderRadius: "8px",
                                      fontSize: "14px",
                                      color: "#4b5563",
                                      boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)",
                                      height: "44px",
                                      '@media (min-width: 640px)': {
                                        padding: "14px 14px 14px 60px",
                                        fontSize: "16px",
                                        height: "50px",
                                      }
                                    }}
                                    buttonStyle={{
                                      border: "none",
                                      background: "transparent",
                                      padding: "0 8px",
                                      borderRadius: "8px 0 0 8px",
                                      height: "44px",
                                      '@media (min-width: 640px)': {
                                        height: "50px",
                                      }
                                    }}
                                    containerClass="mb-2"
                                    inputClass="focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                    dropdownClass="phone-dropdown"
                                    searchPlaceholder="Search country"
                                    enableSearch={true}
                                    searchStyle={{
                                      padding: "8px 10px",
                                      fontSize: "14px",
                                      border: "1px solid #d1d5db",
                                      borderRadius: "6px",
                                      margin: "4px",
                                    }}
                                    dropdownStyle={{
                                      width: "300px",
                                      maxHeight: "300px",
                                      margin: "4px 0",
                                      padding: "4px",
                                      borderRadius: "8px",
                                    }}
                                  />
                                </div>
                              </div>
                            </div>

                            {/* Promo Code Section */}
                            <div className=" p-4 rounded-lg ">
                              <PromoCodeInput
                                state={state}
                                dispatch={dispatch}
                                applyPromoCode={applyPromoCode}
                                promoError={promoError}
                                promoSuccess={promoSuccess}
                                discountAmount={discountAmount}
                              />
                            </div>
                          </motion.div>
                        )}



                        {step === 6 && (
                          <>

                            <Schedule state={state} dispatch={dispatch} />
                          </>
                        )}



                        {step === 7 && (
                          <>
                            <div className="mb-4 sm:mb-8">
                              {/* Header */}
                              <div className="flex items-center justify-between pb-2 sm:pb-4">
                                <h3 className="text-base sm:text-xl text-gray-600 flex items-center">
                                  <FontAwesomeIcon icon={faPaw} className="text-purple-600 mr-2 w-4 h-4 sm:w-5 sm:h-5" />
                                  Locations 🗺️
                                </h3>
                                <button
                                  onClick={() => setStep(1)}
                                  className="ml-2 sm:ml-4 text-primary hover:text-accent transition-colors"
                                >
                                  <FontAwesomeIcon icon={faPen} className="h-3 w-3 sm:h-4 sm:w-4" />
                                </button>
                              </div>

                              {/* Content Card */}
                              <div className="bg-white p-3 sm:p-4 rounded-lg space-y-3 sm:space-y-4 shadow-md border-l-4 sm:border-l-8 border-purple-300">
                                {/* Pricing Info Section */}
                                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center border-b pb-3 sm:pb-4 border-gray-200">
                                  <div className="mb-3 sm:mb-0">
                                    <p className="text-lg sm:text-2xl font-bold text-tblue mb-1 sm:mb-2">
                                      <strong>Important Pricing Info 📢</strong>
                                    </p>
                                    <p className="text-sm sm:text-xl font-bold text-tblue">
                                      <strong>
                                        To ensure you’re aware, our service has a base rate that applies:
                                      </strong>
                                    </p>
                                  </div>
                                  <div className="self-end sm:self-auto">
                                    <p className="text-base sm:text-lg text-gray-800">
                                      <strong>Base Rate:</strong>{" "}
                                      <span className="text-purple-600 font-semibold">
                                        {`${permission.PAW_WALK_BASE_RATE} ${CURRENCY_CODE}`}
                                      </span>
                                    </p>
                                  </div>
                                </div>

                                {/* Location Details */}
                                <div className="flex items-start">
                                  <FontAwesomeIcon
                                    icon={faMapMarkerAlt}
                                    className="text-purple-600 w-4 h-4 sm:w-6 sm:h-6 mt-1 sm:mt-0 mr-3 sm:mr-4"
                                  />
                                  <div className="flex-1">
                                    <p className="text-sm sm:text-base text-gray-800 font-semibold mb-1">
                                      Pickup Location
                                    </p>
                                    <p className="text-xs sm:text-sm text-gray-600 break-words">
                                      {state.pickup_address || "Pickup address not set"}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>


                            <div className="mb-4 sm:mb-8">
                              {/* Header */}
                              <div className="flex justify-between items-center border-b pb-3 sm:pb-4 border-gray-200">
                                <h3 className="text-base sm:text-xl text-gray-600 flex items-center">
                                  <FontAwesomeIcon
                                    icon={faPaw}
                                    className="text-purple-600 mr-2 w-4 h-4 sm:w-5 sm:h-5"
                                  />
                                  Selected Pets
                                </h3>
                                <button
                                  onClick={() => setStep(2)}
                                  className="ml-2 sm:ml-4 text-primary hover:text-accent transition-colors"
                                >
                                  <FontAwesomeIcon icon={faPen} className="h-3 w-3 sm:h-4 sm:w-4" />
                                </button>
                              </div>

                              {/* Pets Grid */}
                              <div className="p-3 sm:p-4 rounded-md shadow-sm">
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                                  {pets
                                    .filter((pet) => state.pet_ids.includes(pet.id))
                                    .map((pet) => (
                                      <div
                                        key={pet.id}
                                        className="flex items-center space-x-3 sm:space-x-4 bg-white p-2 sm:p-3 rounded-lg shadow hover:shadow-md transition-shadow duration-200"
                                      >
                                        <img
                                          src={pet.primary_image_url}
                                          alt={pet.name}
                                          className="w-12 h-12 sm:w-16 sm:h-16 rounded-full object-cover flex-shrink-0"
                                        />
                                        <div className="min-w-0 flex-1">
                                          <p className="text-sm sm:text-base text-gray-700 font-semibold truncate">
                                            {pet.name}
                                          </p>
                                          <p className="text-xs sm:text-sm text-gray-500 truncate">
                                            {pet.breed}
                                          </p>
                                        </div>
                                      </div>
                                    ))}
                                </div>
                              </div>
                            </div>


                            <div className="mb-4 sm:mb-8">
                              {/* Header */}
                              <div className="flex justify-between items-center border-b pb-3 sm:pb-4 border-gray-200">
                                <h3 className="text-base sm:text-xl text-gray-600 flex items-center">
                                  <FontAwesomeIcon
                                    icon={faPaw}
                                    className="text-purple-600 mr-2 w-4 h-4 sm:w-5 sm:h-5"
                                  />
                                  Walk Duration
                                </h3>
                                <button
                                  onClick={() => setStep(3)}
                                  className="ml-2 sm:ml-4 text-primary hover:text-accent transition-colors"
                                >
                                  <FontAwesomeIcon icon={faPen} className="h-3 w-3 sm:h-4 sm:w-4" />
                                </button>
                              </div>

                              {/* Duration Display Card */}
                              <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm border-l-4 sm:border-l-8 border-purple-300">
                                <div className="flex items-center">
                                  <FontAwesomeIcon
                                    icon={faClock}
                                    className="text-purple-600 w-4 h-4 sm:w-5 sm:h-5 mr-2 sm:mr-3"
                                  />
                                  <span className="text-sm sm:text-base text-gray-600">
                                    <strong>{state.duration_walk}</strong>
                                  </span>
                                </div>
                              </div>
                            </div>


                            {charges.tool > 0 && (
                              <div className="mb-4 sm:mb-8">
                                {/* Header */}
                                <div className="flex justify-between items-center border-b pb-3 sm:pb-4 border-gray-200">
                                  <h3 className="text-base sm:text-xl text-gray-600 flex items-center">
                                    <FontAwesomeIcon
                                      icon={faPaw}
                                      className="text-purple-600 mr-2 w-4 h-4 sm:w-5 sm:h-5"
                                    />
                                    Selected Tools
                                  </h3>
                                  <button
                                    onClick={() => setStep(4)}
                                    className="ml-2 sm:ml-4 text-primary hover:text-accent transition-colors"
                                  >
                                    <FontAwesomeIcon icon={faPen} className="h-3 w-3 sm:h-4 sm:w-4" />
                                  </button>
                                </div>

                                {/* Tools Grid */}
                                <div className="p-3 sm:p-4 rounded-md shadow-sm">
                                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                                    {tools
                                      .filter((tool) => state.tool_ids.includes(tool.id))
                                      .map((tool) => (
                                        <div
                                          key={tool.id}
                                          className="flex items-center space-x-3 sm:space-x-4 bg-white p-2 sm:p-3 rounded-lg shadow hover:shadow-md transition-shadow duration-200"
                                        >
                                          <FontAwesomeIcon
                                            icon={faToolbox}
                                            className="text-purple-600 w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0"
                                          />
                                          <div className="min-w-0 flex-1">
                                            <p className="text-sm sm:text-base text-gray-700 font-semibold truncate">
                                              {tool.name}
                                            </p>
                                            <p className="text-xs sm:text-sm text-gray-500">
                                              {tool.price} {CURRENCY_CODE}
                                            </p>
                                          </div>
                                        </div>
                                      ))}
                                  </div>
                                </div>
                              </div>
                            )}


                            <div className="mb-4 sm:mb-8">
                              {/* Header */}
                              <div className="flex justify-between items-center border-b pb-3 sm:pb-4 border-gray-200">
                                <h3 className="text-base sm:text-xl text-gray-600 flex items-center">
                                  <FontAwesomeIcon
                                    icon={faPaw}
                                    className="text-purple-600 mr-2 w-4 h-4 sm:w-5 sm:h-5"
                                  />
                                  Additional Details
                                </h3>
                                <button
                                  onClick={() => setStep(5)}
                                  className="ml-2 sm:ml-4 text-primary hover:text-accent transition-colors"
                                >
                                  <FontAwesomeIcon icon={faPen} className="h-3 w-3 sm:h-4 sm:w-4" />
                                </button>
                              </div>

                              {/* Details Card */}
                              <div className="bg-white p-3 sm:p-4 rounded-lg space-y-3 sm:space-y-4 shadow-md border-l-4 sm:border-l-8 border-purple-300">
                                {state.clean_pet && (
                                  <div className="flex items-center">
                                    <FontAwesomeIcon
                                      icon={faBath}
                                      className="text-purple-600 w-4 h-4 sm:w-5 sm:h-5 mr-2 sm:mr-3"
                                    />
                                    <span className="text-sm sm:text-base text-gray-600">
                                      <strong>Pet Cleaning:</strong> Yes
                                    </span>
                                  </div>
                                )}

                                {state.emergency_no && (
                                  <div className="flex items-center">
                                    <FontAwesomeIcon
                                      icon={faPhoneAlt}
                                      className="text-purple-600 w-4 h-4 sm:w-5 sm:h-5 mr-2 sm:mr-3"
                                    />
                                    <span className="text-sm sm:text-base text-gray-600">
                                      <strong>Emergency Contact:</strong>{" "}
                                      <span className="break-all">{state.emergency_no}</span>
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>


                            {(state.scheduleType == 'slot' || state.scheduleType == 'datetime') && (
                              <div className="mb-4 sm:mb-6">
                                {/* Header */}
                                <div className="flex justify-between items-center border-b pb-3 sm:pb-4 border-gray-200">
                                  <h3 className="text-base sm:text-xl text-gray-600 flex items-center">
                                    <FontAwesomeIcon
                                      icon={faPaw}
                                      className="text-purple-600 mr-2 w-4 h-4 sm:w-5 sm:h-5"
                                    />
                                    Schedule
                                  </h3>
                                  <button
                                    onClick={() => setStep(6)}
                                    className="ml-2 sm:ml-4 text-primary hover:text-accent transition-colors"
                                  >
                                    <FontAwesomeIcon icon={faPen} className="h-3 w-3 sm:h-4 sm:w-4" />
                                  </button>
                                </div>

                                {/* Schedule Details Card */}
                                <div className="bg-white p-3 sm:p-4 rounded-lg space-y-3 sm:space-y-4 shadow-md border-l-4 sm:border-l-8 border-purple-300">
                                  {(state.scheduleType == 'slot' && state.day) && (
                                    <div className="flex items-center">
                                      <FontAwesomeIcon
                                        icon={faCalendarAlt}
                                        className="text-purple-600 w-4 h-4 sm:w-5 sm:h-5 mr-2 sm:mr-3"
                                      />
                                      <span className="text-sm sm:text-base text-gray-700">
                                        <strong>Day:</strong> {state.day}
                                      </span>
                                    </div>
                                  )}

                                  {(state.scheduleType == 'slot' && state.walkSlot) && (
                                    <div className="flex items-center">
                                      <FontAwesomeIcon
                                        icon={faClock}
                                        className="text-purple-600 w-4 h-4 sm:w-5 sm:h-5 mr-2 sm:mr-3"
                                      />
                                      <span className="text-sm sm:text-base text-gray-700">
                                        <strong>Slot:</strong> {convertToAmPm(state.walkSlot)}
                                      </span>
                                    </div>
                                  )}

                                  {(state.scheduleType == 'datetime' && state.scheduled_at) && (
                                    <div className="flex items-center">
                                      <FontAwesomeIcon
                                        icon={faClock}
                                        className="text-purple-600 w-4 h-4 sm:w-5 sm:h-5 mr-2 sm:mr-3"
                                      />
                                      <span className="text-sm sm:text-base text-gray-700">
                                        <strong>Scheduled Time:</strong>{" "}
                                        {format(new Date(state.scheduled_at), 'PPpp')}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}


                            <div className="mb-4 sm:mb-8">
                              <label
                                htmlFor="additionalInfo"
                                className="block text-base sm:text-xl text-gray-600 mb-2 sm:mb-3"
                              >
                                <FontAwesomeIcon
                                  icon={faPaw}
                                  className="text-purple-600 mr-2 w-4 h-4 sm:w-5 sm:h-5"
                                />
                                Any Additional Information You'll Like To Share
                              </label>
                              <textarea
                                id="additionalInfo"
                                rows="4"
                                value={state.additional_info || ""}
                                onChange={(e) =>
                                  dispatch({
                                    type: "UPDATE_FIELD",
                                    field: "additional_info",
                                    payload: e.target.value,
                                  })
                                }
                                className="block w-full px-3 sm:px-4 py-2 sm:py-3 
                                  text-sm sm:text-base
                                  border border-gray-300 
                                  rounded-lg shadow-sm
                                  focus:ring-2 focus:ring-purple-500 focus:border-purple-500
                                  transition-all duration-200
                                  placeholder:text-sm sm:placeholder:text-base"
                                placeholder="Enter any additional details or special instructions..."
                              />
                            </div>



                            <div className="mb-4 sm:mb-8">
                              {/* Header */}
                              <h3 className="text-base sm:text-xl text-gray-600 mb-3 sm:mb-4 flex items-center">
                                <FontAwesomeIcon
                                  icon={faPaw}
                                  className="text-purple-600 mr-2 w-4 h-4 sm:w-5 sm:h-5"
                                />
                                Cost Breakdown
                              </h3>

                              {/* Cost Breakdown Card */}
                              <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md border-l-4 sm:border-l-8 border-purple-300">
                                {/* Free Walk Alert */}
                                {applyFreeWalk && (
                                  <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-purple-50 border-l-4 border-purple-400 rounded-md flex items-center">
                                    <FontAwesomeIcon
                                      icon={faPaw}
                                      className="text-purple-500 mr-2 sm:mr-3 w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0"
                                    />
                                    <span className="text-sm sm:text-base text-purple-800 font-semibold">
                                      Congratulations! Your walk is fully covered with your membership.
                                      You'll only be charged for add-ons such as pet gear, cleaning, or extra pets.🎉
                                    </span>
                                  </div>
                                )}

                                {/* Service Charges Section */}
                                <div className="mb-4">
                                  <h4 className="text-base sm:text-lg font-semibold text-gray-700 mb-2 sm:mb-3">
                                    Service Charges
                                  </h4>
                                  <div className="space-y-2 sm:space-y-3">
                                    {Object.keys(charges).map((key, index) => {
                                      const chargeValue = charges[key];
                                      if (key === "base_rate" || chargeValue <= 0) return null;

                                      const isChargeFree = freeChargeKeys.includes(key);
                                      const label = getServiceLabel(key);
                                      const formattedCharge = (Number(chargeValue) || 0).toFixed(2);
                                      return (
                                        <div key={index} className="flex items-center justify-between">
                                          <span className={`text-${isChargeFree ? 'sm' : 'sm '} sm:text-base text-gray-600 flex items-center`}>
                                            <FontAwesomeIcon
                                              icon={faPaw}
                                              className={`text-${!isChargeFree ? 'yellow-500' : 'tblue'}  mr-2 w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0`}
                                            />
                                            {label}:
                                          </span>
                                          <div className="inline-flex items-center">
                                            {applyFreeWalk && isChargeFree ? (
                                              <>
                                                <span className="text-sm sm:text-base text-green-500 font-semibold">FREE </span>
                                                <span className="text-sm sm:text-base text-gray-800 line-through ml-1">
                                                  <strong>{formattedCharge} {CURRENCY_CODE}</strong>
                                                </span>
                                              </>
                                            ) : (
                                              <span className="text-sm sm:text-base text-gray-800">
                                                <strong>{formattedCharge} {CURRENCY_CODE}</strong>
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>

                                {/* Totals Section */}
                                <div className="space-y-3">
                                  {/* Service Total */}
                                  <div className="flex items-center justify-between border-t pt-3 text-sm sm:text-base">
                                    <span className="text-gray-600 flex items-center">
                                      <FontAwesomeIcon icon={faPaw} className="text-tblue mr-2 w-4 h-4 sm:w-5 sm:h-5" />
                                      Service Total:
                                    </span>
                                    <span className="text-gray-800">
                                      <strong>
                                        {total.toFixed(2)} {CURRENCY_CODE}
                                      </strong>
                                    </span>
                                  </div>

                                  {/* Base Rate */}
                                  <div className="flex items-center justify-between pt-3 text-sm sm:text-base">
                                    <span className="text-gray-600 flex items-center">
                                      <FontAwesomeIcon icon={faPaw} className="text-tblue mr-2 w-4 h-4 sm:w-5 sm:h-5" />
                                      Base Rate:
                                    </span>
                                    <div className="inline-flex items-center">
                                      {applyFreeWalk ? (
                                        <>
                                          <span className="text-green-500 font-semibold">FREE</span>
                                          <span className="text-gray-800 line-through ml-1">
                                            <strong>{baseRate.toFixed(2)} {CURRENCY_CODE}</strong>
                                          </span>
                                        </>
                                      ) : (
                                        <span className="text-gray-800">
                                          <strong>+{baseRate.toFixed(2)} {CURRENCY_CODE}</strong>
                                        </span>
                                      )}
                                    </div>
                                  </div>

                                  {/* Discount Section */}
                                  {discountAmount > 0 && (
                                    <>
                                      {!applyFreeWalk && (
                                        <div className="flex items-center justify-between mt-3 border-t pt-3 text-sm sm:text-base">
                                          <span className="text-gray-600 flex items-center">
                                            <FontAwesomeIcon icon={faPaw} className="text-gray-500 mr-2 w-4 h-4 sm:w-5 sm:h-5" />
                                            Original Total (Including Base Rate):
                                          </span>
                                          <span className="text-gray-800">
                                            <strong>{(total + baseRate).toFixed(2)} {CURRENCY_CODE}</strong>
                                          </span>
                                        </div>
                                      )}
                                      <div className="flex items-center justify-between mt-2 text-green-600 text-sm sm:text-base">
                                        <span className="flex items-center">
                                          <FontAwesomeIcon icon={faPaw} className="text-green-500 mr-2 w-4 h-4 sm:w-5 sm:h-5" />
                                          Discount Applied{discount?.code ? ` (${discount.code})` : ""}:
                                        </span>
                                        <strong>-{discountAmount.toFixed(2)} {CURRENCY_CODE}</strong>
                                      </div>
                                    </>
                                  )}

                                  {/* Total After Discount */}
                                  <div className="flex items-center justify-between mt-4 border-t pt-3 text-sm sm:text-base">
                                    <span className="text-gray-600 flex items-center">
                                      <FontAwesomeIcon icon={faPaw} className="text-green-600 mr-2 w-4 h-4 sm:w-5 sm:h-5" />
                                      Total {discountAmount > 0 ? "(After Discount)" : ""}:
                                    </span>
                                    <div className="inline-flex items-center">
                                      {applyFreeWalk ? (
                                        <span className="text-gray-800 mr-1">
                                          <strong>{(total - discountAmount).toFixed(2)} {CURRENCY_CODE}</strong>
                                        </span>
                                      ) : (
                                        <span className="text-gray-800">
                                          <strong>{(total + baseRate - discountAmount).toFixed(2)} {CURRENCY_CODE}</strong>
                                        </span>
                                      )}
                                    </div>
                                  </div>

                                  {/* VAT */}
                                  <div className="flex items-center justify-between pt-3 text-sm sm:text-base">
                                    <span className="text-gray-600 flex items-center">
                                      <FontAwesomeIcon icon={faPaw} className="text-green-600 mr-2 w-4 h-4 sm:w-5 sm:h-5" />
                                      VAT (5%):
                                    </span>
                                    <span className="text-gray-800">
                                      <strong>+{vatAmount.toFixed(2)} {CURRENCY_CODE}</strong>
                                    </span>
                                  </div>

                                  {/* Grand Total */}
                                  <div className="flex items-center justify-between mt-4 pt-3 border-t text-base sm:text-lg font-bold">
                                    <span className="text-purple-600 flex items-center">
                                      <FontAwesomeIcon icon={faPaw} className="mr-2 w-4 h-4 sm:w-5 sm:h-5" />
                                      Grand Total (After VAT):
                                    </span>
                                    <span className="text-gray-800">
                                      <strong>{finalTotal.toFixed(2)} {CURRENCY_CODE}</strong>
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Terms and Conditions Section */}
                            <div className="mb-6 bg-white p-6 rounded-2xl shadow-md border-l-4 border-primary">
                              <div className="space-y-4">

                                {/* Acceptance Checkbox */}
                                <div className="flex items-start space-x-3">
                                  <div className="flex items-center h-5">
                                    <input
                                      id="terms"
                                      name="terms"
                                      type="checkbox"
                                      required
                                      checked={state.termsAccepted}
                                      onChange={(e) =>
                                        dispatch({
                                          type: "UPDATE_FIELD",
                                          field: "termsAccepted",
                                          payload: e.target.checked
                                        })
                                      }
                                      className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary cursor-pointer"
                                    />
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <label htmlFor="terms" className="text-sm text-gray-600 cursor-pointer">
                                      I have read and agree to the{" "}
                                      <a
                                        href={routes.terms}
                                        className="text-primary hover:text-accent underline font-medium"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                      >
                                        Terms of Service
                                      </a>
                                      {" "}and{" "}
                                      <a
                                        href={routes.privacy_policy}
                                        className="text-primary hover:text-accent underline font-medium"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                      >
                                        Privacy Policy
                                      </a>
                                    </label>
                                  </div>
                                </div>

                              </div>
                            </div>
                          </>
                        )}

                        {step === 8 && (
                          <>
                            <WalkPaymentMethodSelection walk={walkData} onCancel={onCancel} asDiv={true} onSave={onSave} />
                          </>
                        )}


                      </div>
                    )}
                    <div className="my-8 flex justify-between">
                      {step > 1 && (
                        <>
                          {step !== 8 && (
                            <button
                              type="button"
                              onClick={handlePrevStep}
                              className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg"
                            >
                              Back
                            </button>
                          )}


                          {step === 7 && (
                            <motion.button
                              type="submit"
                              whileHover={loading ? {} : { scale: 1.05 }}  // Disable hover effect when loading
                              whileTap={loading ? {} : { scale: 0.95 }}    // Disable tap effect when loading
                              disabled={loading}                           // Disable button when submitting
                              className={`font-semibold px-6 py-3 rounded-lg shadow-md transition-all duration-300 ease-in-out 
                             ${loading ? "bg-gray-400 cursor-not-allowed" : "bg-gradient-to-r from-purple-500 to-indigo-600 text-white"}`}
                            >
                              {loading ? (
                                <div className="flex items-center">
                                  <svg
                                    className="animate-spin h-5 w-5 mr-2 text-white"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                  >
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0116 0H4z"></path>
                                  </svg>
                                  Processing...
                                </div>
                              ) : (
                                "Confirm & Submit 🚀"
                              )}
                            </motion.button>

                          )}
                        </>
                      )}
                      {step < 7 && step > 1 && (
                        <button
                          type="button"
                          onClick={handleNextStep}
                          className="bg-primary text-white px-4 py-2 rounded-lg"
                        >
                          Next
                        </button>
                      )}
                    </div>
                  </form>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default WalkRequest;
